import { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { LAST_ROUTE_KEY, ROUTE_URLS } from '../common/v2/constants';
import { useAuthenticationServiceContext } from '../common/v2/context/contexts';

/**
 * Custom hook to maintain the current route across page reloads
 * @param defaultRoute The default landing page to redirect from
 */
export const useRoutePersistence = (defaultRoute: string): void => {
  const location = useLocation();
  const history = useHistory();
  const initialCheckDoneRef = useRef(false);
  const loginRoute = ROUTE_URLS.LOGIN.getFullPath();
  const authService = useAuthenticationServiceContext();

  // Handle the initial redirect on mount
  useEffect(() => {
    // Skip this logic if we're on the login page or performing logout
    if (location.pathname === loginRoute || authService.isPerformingLogout) {
      return;
    }

    // Only run this once when the component mounts
    if (!initialCheckDoneRef.current) {
      initialCheckDoneRef.current = true;

      const savedRoute = sessionStorage.getItem(LAST_ROUTE_KEY);

      // Only redirect if:
      // 1. We're on the default route
      // 2. We have a saved route
      // 3. The saved route is not the default route
      // 4. The saved route is not the login page
      if (
        location.pathname === defaultRoute &&
        savedRoute &&
        savedRoute !== defaultRoute
      ) {
        // Use setTimeout to ensure this happens after React's rendering cycle
        setTimeout(() => {
          history.replace(savedRoute);
        }, 50);
      } else {
        // If we're not redirecting and not on login, save the current route
        sessionStorage.setItem(LAST_ROUTE_KEY, location.pathname);
      }
    }
  }, [defaultRoute, history, location.pathname]);

  // Save route on navigation and before unload
  useEffect(() => {
    // Skip this logic if we're on the login page or performing logout
    if (location.pathname === loginRoute || authService.isPerformingLogout) {
      return;
    }
    // Don't update during the initial check to avoid conflicts
    if (initialCheckDoneRef.current) {
      sessionStorage.setItem(LAST_ROUTE_KEY, location.pathname);
    }

    const handleBeforeUnload = () => {
      // Don't save login page as the last route
      if (location.pathname !== loginRoute && !authService.isPerformingLogout) {
        sessionStorage.setItem(LAST_ROUTE_KEY, location.pathname);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname, authService.isPerformingLogout]);
};
